import type { SliceMetric } from '@death-star/shared';
import { METRIC_LABELS } from './MetricTabs.js';
import { metricDirection, pickFormatter, sampleLabel } from './format.js';

export type SliceStatus = 'on_target' | 'within_20pct' | 'off_target' | 'no_data' | 'observability';

const TARGETS: Record<SliceMetric, { target: string; definition: string; help?: string }> = {
  auto_approval:      {
    target: 'Target: ≥30% (LOW-tier, by 90d)',
    definition: 'Share of PRs the bot auto-approved.',
    help:
      'Share of review runs whose verdict was APPROVE — i.e. Diff Vader’s review was ' +
      'sufficient to unlock merging the PR (whether or not auto-merge was literally toggled). ' +
      'This card counts every review run and includes shadow-mode runs, so it can differ from ' +
      'the Velocity Scorecard’s auto-approval rate, which counts each PR once (its latest ' +
      'verdict) and excludes shadow mode.',
  },
  ttm:                { target: 'Target: -30% vs baseline by 90d', definition: 'TTM = median time-to-merge (in seconds).' },
  ttfr:               { target: 'Target: -50% vs baseline by 90d', definition: 'TTFR = median time-to-first-human-review (in seconds).' },
  revert_rate:        { target: 'Target: ≤ baseline revert rate', definition: 'Share of bot-approved PRs reverted within 7 days.' },
  override_rate:      { target: 'Target: ≤10%', definition: 'Share of PRs where author invoked /request-jedi-review.' },
  council_wall_clock: { target: 'Observability only — no target', definition: 'End-to-end council wall-clock time (p95, in seconds).' },
  findings_per_run: {
    target: 'Observability only — no target',
    definition: 'Findings emitted per council run.',
    help:
      'Deduplicated council findings ÷ council runs. The denominator excludes TOO_LARGE runs ' +
      '(the council short-circuited and reviewed nothing) and re-reviews (verifier mode ' +
      're-checks prior findings rather than producing new ones). Carried-forward re-emissions ' +
      'are counted once, in the run that first raised them.',
  },
  substantive_share: {
    target: 'Observability only — no target',
    definition: 'Share of findings the council marked blocking.',
    help:
      'Substantive findings ÷ all findings. Read this NORMALISED against the council-wide ' +
      'share for the same week: diff-vader#223 dropped the blocking rate council-wide from ' +
      '96.7% (2026-W22) to 21.1% (2026-W31), so a raw per-size-band read across that boundary ' +
      'attributes a council-wide regression to large diffs.',
  },
  severity_mix: {
    target: 'Observability only — no target',
    definition: 'Share of findings rated high or critical severity.',
    help:
      'The bars show the high+critical share; the lines under each tile show the full mix. ' +
      'Every finding stored before 2026-08-12 reads back "unspecified" because death-star’s ' +
      'CouncilFindingSchema did not declare `severity` and zod strips undeclared keys at ' +
      'ingest — a high unspecified share before that date is a schema artifact, not a ' +
      'measurement of the council.',
  },
};

const STATUS_PILL: Record<SliceStatus, { bg: string; fg: string; label: string }> = {
  on_target:     { bg: 'var(--loop-green-100)',   fg: 'var(--loop-green-700)',   label: 'On target' },
  within_20pct:  { bg: 'var(--loop-yellow-100)',  fg: 'var(--loop-yellow-800)',  label: 'Within 20%' },
  off_target:    { bg: 'var(--loop-red-100)',     fg: 'var(--loop-red-700)',     label: 'Off target' },
  no_data:       { bg: 'var(--loop-neutral-200)', fg: 'var(--loop-neutral-700)', label: 'No data' },
  observability: { bg: 'var(--loop-neutral-200)', fg: 'var(--loop-neutral-700)', label: 'Observability' },
};

export interface MetricHeaderProps {
  metric: SliceMetric;
  current: number;
  baseline: number | null;
  delta: number | null;
  sampleN: number;
  status: SliceStatus;
}

export function MetricHeader({ metric, current, baseline, delta, sampleN, status }: MetricHeaderProps) {
  // Use the shared time-metric classifier. A hand-rolled check here that
  // omitted `council_wall_clock` ran its p95-seconds value through the
  // percentage formatter and rendered "25127%".
  const fmt = pickFormatter(metric);
  const meta = TARGETS[metric];
  const pill = STATUS_PILL[status];

  // "Sample (PRs)" is inaccurate wherever a sample is not a PR — an individual
  // duration measurement, a council run, or a finding. The mapping lives in
  // format.ts so it can't drift from the formatter's classification.
  const sampleUnit = sampleLabel(metric);

  // no_data: render the value as an em-dash rather than running 0 through a
  // formatter (which would print a misleading "0%" / "0 m").
  const currentDisplay = status === 'no_data' ? '—' : fmt(current);

  // Color the delta by whether it's an improvement given the metric's
  // direction (lower-is-better vs higher-is-better), not a hardcoded green.
  // delta is the signed change in the value (current − baseline).
  const direction = metricDirection(metric);
  let deltaColor = 'var(--loop-neutral-700)';
  if (delta !== null && delta !== 0) {
    const improved = direction === 'lower' ? delta < 0 : delta > 0;
    deltaColor = improved ? 'var(--loop-green-700)' : 'var(--loop-red-700)';
  }

  const labelStyle = {
    fontSize: '0.7rem',
    color: 'var(--loop-neutral-700)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    fontWeight: 528,
  };
  const valueStyle = {
    fontSize: '1.1rem',
    fontWeight: 664,
    color: 'var(--loop-neutral-1100)',
    marginTop: 2,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '1rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>{METRIC_LABELS[metric]}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)' }}>{meta.target}</div>
      </div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-700)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{meta.definition}</span>
        {meta.help && (
          <span
            role="img"
            aria-label={meta.help}
            title={meta.help}
            style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--loop-neutral-500)', cursor: 'help' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4" />
              <path d="M12 8h.01" />
            </svg>
          </span>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 24,
          padding: '12px 16px',
          background: 'var(--loop-neutral-100)',
          border: '1px solid var(--loop-neutral-300)',
          borderRadius: 'var(--loop-radius-md)',
          marginBottom: 16,
          fontSize: '0.8125rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={labelStyle}>Current</div>
          <div style={valueStyle}>{currentDisplay}</div>
          {delta !== null && baseline !== null && (
            <div style={{ fontSize: '0.75rem', color: deltaColor, fontWeight: 528, marginTop: 2 }}>
              {(delta > 0 ? '+' : '')}{fmt(delta)} vs baseline
            </div>
          )}
        </div>
        <div>
          <div style={labelStyle}>{sampleUnit}</div>
          <div style={valueStyle}>{sampleN}</div>
        </div>
        <div>
          <div style={labelStyle}>Status</div>
          <span
            style={{
              display: 'inline-block',
              padding: '2px 10px',
              borderRadius: 999,
              fontSize: '0.75rem',
              fontWeight: 528,
              background: pill.bg,
              color: pill.fg,
              marginTop: 4,
            }}
          >
            {pill.label}
          </span>
        </div>
      </div>
    </div>
  );
}
